<template>
  <div class="editor-style-settings-mask" @click.self="handleCancel">
    <div class="editor-style-settings-dialog">
      <header class="editor-style-settings-header">
        <h1>{{ $t("editorStyleSettingsModal.title") }}</h1>
        <button type="button" class="close-button" :aria-label="$t('editorStyleSettingsModal.closeAriaLabel')" @click="handleCancel">×</button>
      </header>
      <div class="editor-style-settings-body">
        <div class="editor-style-settings-form">
          <slider-input
            :label="$t('editorStyleSettingsModal.fontSize')"
            :value="formFontSize"
            :min="limits.fontSize.min"
            :max="limits.fontSize.max"
            :step="0.1"
            unit="pt"
            @input="updateFontSize"
          />
          <slider-input
            :label="$t('editorStyleSettingsModal.lineHeight')"
            :value="formLineHeight"
            :min="limits.lineHeight.min"
            :max="limits.lineHeight.max"
            :step="0.1"
            @input="updateLineHeight"
          />
          <slider-input
            :label="$t('editorStyleSettingsModal.letterSpacing')"
            :value="formLetterSpacing"
            :min="limits.letterSpacing.min"
            :max="limits.letterSpacing.max"
            :step="0.1"
            unit="pt"
            @input="updateLetterSpacing"
          />
        </div>
        <div class="editor-style-settings-preview" :style="previewStyle">
          {{ sampleText }}
        </div>
      </div>
      <footer class="editor-style-settings-footer">
        <button type="button" class="button secondary" @click="handleReset">
          {{ $t("editorStyleSettingsModal.resetToDefault") }}
        </button>
        <button type="button" class="button primary" @click="handleApply">
          {{ $t("editorStyleSettingsModal.apply") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import SliderInput from "@/components/atoms/SliderInput.vue";
import { useEditorStyleSettings } from "@/composables/useEditorStyleSettings";
import type { EditorStyleSettings } from "@/composables/useEditorStyleSettings";

const SAMPLE_TEXT =
  "吾輩は猫である。名前はまだ無い。どこで生れたかとんと見当がつかぬ。何でも薄暗いじめじめした所でニャーニャー泣いていた事だけは記憶している。";

export default Vue.extend({
  name: "EditorStyleSettingsModal",
  components: { SliderInput },
  props: {
    /** モーダルを開いた画面（analytics用）: 'writing' | 'multi_writing' */
    source: {
      type: String as () => "writing" | "multi_writing",
      default: "writing",
    },
  },
  setup() {
    const { settings, loadSettings, saveSettings, defaults, limits } = useEditorStyleSettings();
    return {
      settings,
      loadSettings,
      saveSettings,
      defaults,
      limits,
    };
  },
  data() {
    return {
      sampleText: SAMPLE_TEXT,
      /** モーダル内の下書き（プレビュー・スライダーはこれに紐づけ Vue 2 の reactivity で確実に更新する） */
      draft: {
        fontSize: 12,
        lineHeight: 1.8,
        letterSpacing: 0,
      },
    };
  },
  computed: {
    formFontSize(): number {
      return this.draft.fontSize;
    },
    formLineHeight(): number {
      return this.draft.lineHeight;
    },
    formLetterSpacing(): number {
      return this.draft.letterSpacing;
    },
    previewStyle(): Record<string, string | number> {
      return {
        fontSize: `${this.draft.fontSize}pt`,
        lineHeight: this.draft.lineHeight,
        letterSpacing: `${this.draft.letterSpacing}pt`,
      };
    },
  },
  mounted() {
    this.loadSettings();
    this.syncDraftFromSettings();
    this.trackModalOpen();
  },
  methods: {
    syncDraftFromSettings() {
      const s = this.settings as EditorStyleSettings;
      this.draft.fontSize = s.fontSize;
      this.draft.lineHeight = s.lineHeight;
      this.draft.letterSpacing = s.letterSpacing;
    },
    updateFontSize(v: number) {
      this.draft.fontSize = v;
    },
    updateLineHeight(v: number) {
      this.draft.lineHeight = v;
    },
    updateLetterSpacing(v: number) {
      this.draft.letterSpacing = v;
    },
    trackModalOpen() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_modal_open",
          source: this.source,
        });
      }
    },
    trackApply() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_changed",
          font_size: this.draft.fontSize,
          line_height: this.draft.lineHeight,
          letter_spacing: this.draft.letterSpacing,
          source: this.source,
        });
      }
    },
    trackReset() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_reset",
          source: this.source,
        });
      }
    },
    trackCancel() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_cancel",
          source: this.source,
        });
      }
    },
    handleApply() {
      this.saveSettings(this.draft);
      this.trackApply();
      this.$close(true);
    },
    /** モーダル内の設定値のみデフォルトに戻す。保存は適用ボタンで行う。 */
    handleReset() {
      this.draft.fontSize = this.defaults.fontSize;
      this.draft.lineHeight = this.defaults.lineHeight;
      this.draft.letterSpacing = this.defaults.letterSpacing;
      this.trackReset();
    },
    handleCancel() {
      this.trackCancel();
      this.$close(false);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.editor-style-settings-mask {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.editor-style-settings-dialog {
  width: 90%;
  max-width: 440px;
  max-height: calc(100vh - 24px);
  margin: 10px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.editor-style-settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);

  h1 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: var(--font-weight-bold);
  }

  .close-button {
    position: relative;
    padding: 4px 8px;
    font-size: 1.5rem;
    line-height: 1;
    color: var(--color-text-low);
    background: none;
    border: none;
    cursor: pointer;
    @include hover-overlay;

    &:hover {
      color: var(--color-text-normal);
    }
  }
}

.editor-style-settings-body {
  padding: 20px;
  border-bottom: 1px solid var(--color-border);
  min-height: 0;
  overflow-y: auto;
}

.editor-style-settings-form {
  margin-bottom: 1.25rem;
}

.editor-style-settings-preview {
  padding: 16px;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-subtle);
  font-family: inherit;
  min-height: 80px;
}

.editor-style-settings-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;

  .button {
    position: relative;
    padding: 10px 20px;
    border-radius: var(--radius-sm);
    font-size: 0.95rem;
    font-weight: var(--font-weight-bold);
    cursor: pointer;
    @include hover-overlay;

    &.secondary {
      color: var(--color-text-normal);
      background-color: var(--color-surface-muted);
      border: 1px solid var(--color-border);
    }

    &.primary {
      color: var(--color-text-on-fixed-bg);
      background-color: var(--color-primary);
      border: none;
    }
  }
}
</style>
